import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session timeout in milliseconds (15 minutes)
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('icrcl_token'));
  const [loading, setLoading] = useState(true);
  const roleVersionRef = useRef(null);
  const roleCheckIntervalRef = useRef(null);
  const sessionTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const api = axios.create({
    baseURL: API_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('icrcl_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  // Add response interceptor to detect role mismatches
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      // If we get 401/403 due to role change, force refresh
      if (error.response?.status === 401 || error.response?.status === 403) {
        const errorDetail = error.response?.data?.detail || '';
        if (errorDetail.includes('role') || errorDetail.includes('permission')) {
          await checkRoleVersion();
        }
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      const userData = response.data;
      
      // Store initial role version
      if (userData.role_version !== undefined) {
        roleVersionRef.current = userData.role_version;
      }
      
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('icrcl_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Check if role has changed on the server
  const checkRoleVersion = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await api.get('/auth/check-role-version');
      const { role, role_version } = response.data;
      
      // If role version changed, force refresh user data
      if (roleVersionRef.current !== null && 
          role_version !== undefined && 
          role_version !== roleVersionRef.current) {
        console.log('Role version changed, refreshing user data...');
        roleVersionRef.current = role_version;
        
        // Force refresh user data
        const userResponse = await api.get('/auth/me');
        setUser(userResponse.data);
        
        // Show notification about role change
        return { roleChanged: true, newRole: role };
      }
      
      // Also check if role doesn't match current user
      if (user && role !== user.role) {
        console.log('Role mismatch detected, refreshing...');
        const userResponse = await api.get('/auth/me');
        setUser(userResponse.data);
        return { roleChanged: true, newRole: role };
      }
      
      return { roleChanged: false };
    } catch (error) {
      console.error('Failed to check role version:', error);
      return { roleChanged: false };
    }
  }, [token, user]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Poll for role changes every 30 seconds (only when user is logged in)
  useEffect(() => {
    if (user && token) {
      // Start polling
      roleCheckIntervalRef.current = setInterval(async () => {
        const result = await checkRoleVersion();
        if (result?.roleChanged) {
          // Optionally trigger a page reload or show notification
          console.log('User role has been updated to:', result.newRole);
        }
      }, 30000); // Check every 30 seconds
      
      return () => {
        if (roleCheckIntervalRef.current) {
          clearInterval(roleCheckIntervalRef.current);
        }
      };
    }
  }, [user, token, checkRoleVersion]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData, mfa_required } = response.data;
    
    // If MFA is required, don't set token yet - let component handle it
    if (mfa_required) {
      return response.data;
    }
    
    localStorage.setItem('icrcl_token', access_token);
    setToken(access_token);
    setUser(userData);
    roleVersionRef.current = userData.role_version || 0;
    return userData;
  };

  // Verify MFA code and complete login
  const verifyMfa = async (userId, code) => {
    const response = await api.post('/auth/verify-mfa', { user_id: userId, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('icrcl_token', access_token);
    setToken(access_token);
    setUser(userData);
    roleVersionRef.current = userData.role_version || 0;
    resetActivityTimer(); // Start session timer
    return userData;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    // Check if this is a full registration (main admin) or pending approval
    if (response.data.access_token) {
      const { access_token, user: newUser } = response.data;
      localStorage.setItem('icrcl_token', access_token);
      setToken(access_token);
      setUser(newUser);
      resetActivityTimer(); // Start session timer
      return newUser;
    }
    // For pending approval registrations, just return the response data
    return response.data;
  };

  // Session timeout handling
  const resetActivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timeout
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    
    // Set new timeout only if user is logged in
    if (token && user) {
      sessionTimeoutRef.current = setTimeout(() => {
        handleSessionTimeout();
      }, SESSION_TIMEOUT_MS);
    }
  }, [token, user]);

  const handleSessionTimeout = useCallback(() => {
    // Log out the user due to inactivity
    localStorage.removeItem('icrcl_token');
    setToken(null);
    setUser(null);
    roleVersionRef.current = null;
    
    // Clear all intervals
    if (roleCheckIntervalRef.current) {
      clearInterval(roleCheckIntervalRef.current);
    }
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    
    // Store timeout flag for display message
    sessionStorage.setItem('session_timeout', 'true');
    
    // Redirect to login
    window.location.href = '/login';
  }, []);

  // Activity listeners for session timeout
  useEffect(() => {
    if (!token || !user) return;
    
    const handleActivity = () => {
      resetActivityTimer();
    };
    
    // Listen for user activity
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Start initial timer
    resetActivityTimer();
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
    };
  }, [token, user, resetActivityTimer]);

  const logout = () => {
    localStorage.removeItem('icrcl_token');
    setToken(null);
    setUser(null);
    roleVersionRef.current = null;
    if (roleCheckIntervalRef.current) {
      clearInterval(roleCheckIntervalRef.current);
    }
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
  };

  // Function to refresh user data (used after profile update)
  // Set auth data externally (for WebAuthn login)
  const setAuthData = useCallback((accessToken, userData) => {
    localStorage.setItem('icrcl_token', accessToken);
    setToken(accessToken);
    setUser(userData);
    roleVersionRef.current = userData.role_version || 0;
    resetActivityTimer();
  }, [resetActivityTimer]);

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      if (response.data.role_version !== undefined) {
        roleVersionRef.current = response.data.role_version;
      }
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  // Helper to get cache-busted profile image URL
  const getProfileImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    // Strip existing query params and add fresh timestamp
    const baseUrl = imageUrl.split('?')[0];
    return `${baseUrl}?v=${Date.now()}`;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    verifyMfa,
    setAuthData,
    api,
    refreshUser,
    checkRoleVersion,
    getProfileImageUrl,
    isDirector: user?.role === 'director' || user?.role === 'managing_director',
    isManagingDirector: user?.role === 'managing_director',
    isClinicalLead: user?.role === 'clinical_lead',
    isTherapist: user?.role === 'therapist',
    isRehabAssistant: user?.role === 'rehab_assistant',
    isAdmin: user?.role === 'admin',
    isFinance: user?.role === 'finance',
    isHR: user?.role === 'hr',
    canAccessFinance: ['director', 'managing_director', 'admin', 'finance'].includes(user?.role),
    canAccessHR: ['director', 'managing_director', 'admin', 'hr'].includes(user?.role),
    canAccessAudit: ['director', 'managing_director', 'admin'].includes(user?.role),
    canAccessClinical: ['director', 'managing_director', 'clinical_lead', 'therapist', 'rehab_assistant'].includes(user?.role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
